/**
 * PredictedClosureTile.tsx — STEP-OV-PHASE-PROGRESS-TRUTH
 * Shows burn-rate, exit-gate checklist, and milestone trajectories.
 * UX-031: 44×44px minimum touch targets.
 */
import type { PhaseProgressResponse } from "../api/types.js";

type ExitGateItem = PhaseProgressResponse["predicted_closure"]["exit_gate_checklist"][number];
type MilestoneTrajectory = PhaseProgressResponse["predicted_closure"]["milestone_trajectories"][number];

interface Props {
  data: PhaseProgressResponse;
}

function statusIcon(s: ExitGateItem["status"]): string {
  if (s === "met") return "✓";
  if (s === "not_met") return "✗";
  return "?";
}

function statusColor(s: ExitGateItem["status"]): string {
  if (s === "met") return "var(--ok)";
  if (s === "not_met") return "var(--danger)";
  return "var(--text-dim)";
}

function MilestoneTrajectoryRow({ m }: { m: MilestoneTrajectory }) {
  const pct = m.completion_pct;
  const barColor = pct >= 80 ? "var(--ok)" : pct >= 50 ? "var(--accent)" : "var(--warn)";

  return (
    <div
      style={{
        padding: "8px 0",
        borderBottom: "1px solid var(--border-soft)",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "baseline",
          marginBottom: 4,
        }}
      >
        <div style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--text)", fontWeight: 600 }}>
          {m.title}
        </div>
        <div style={{ fontFamily: "var(--font-mono)", fontSize: 10, color: barColor, fontWeight: 700 }}>
          {pct}%
        </div>
      </div>
      {/* Progress bar */}
      <div
        style={{
          height: 4,
          background: "var(--border-soft)",
          borderRadius: 2,
          overflow: "hidden",
          marginBottom: 4,
        }}
      >
        <div
          style={{
            height: "100%",
            width: `${Math.min(pct, 100)}%`,
            background: barColor,
            borderRadius: 2,
            transition: "width 0.3s ease",
          }}
        />
      </div>
      <div
        style={{
          display: "flex",
          gap: 12,
          fontFamily: "var(--font-mono)",
          fontSize: 9,
          color: "var(--text-dim)",
        }}
      >
        <span>{m.steps_remaining} remaining</span>
        <span>{m.steps_complete}/{m.steps_total} done</span>
        {m.po_review_state !== "none" && (
          <span>PO: {m.po_review_state}</span>
        )}
      </div>
    </div>
  );
}

export function PredictedClosureTile({ data }: Props) {
  const { burn_rate, exit_gate_checklist, milestone_trajectories } = data.predicted_closure;

  // Estimated days to close = open_steps / daily_avg
  const openSteps = data.close_distance.open_steps;
  const daysToClose =
    burn_rate.daily_avg > 0 ? Math.ceil(openSteps / burn_rate.daily_avg) : null;

  return (
    <div data-testid="predicted-closure-tile" style={{ padding: "8px" }}>
      {/* Burn rate */}
      <div className="card" style={{ padding: "12px 16px", marginBottom: 8 }}>
        <div
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: 9,
            color: "var(--text-dim)",
            textTransform: "uppercase",
            letterSpacing: "0.05em",
            marginBottom: 8,
          }}
        >
          Burn rate — {burn_rate.source}
        </div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            gap: 8,
          }}
        >
          {[
            { label: "daily avg", value: burn_rate.daily_avg.toFixed(1) },
            { label: "weekly avg", value: burn_rate.weekly_avg.toFixed(1) },
            { label: daysToClose != null ? "days to close" : "completion", value: daysToClose != null ? String(daysToClose) : "—" },
          ].map((stat) => (
            <div key={stat.label} style={{ textAlign: "center" }}>
              <div
                style={{
                  fontFamily: "var(--font-mono)",
                  fontSize: 20,
                  fontWeight: 700,
                  color: "var(--text)",
                  lineHeight: 1,
                }}
              >
                {stat.value}
              </div>
              <div
                style={{
                  fontFamily: "var(--font-mono)",
                  fontSize: 8,
                  color: "var(--text-dim)",
                  textTransform: "uppercase",
                  marginTop: 4,
                }}
              >
                {stat.label}
              </div>
            </div>
          ))}
        </div>
        <div
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: 9,
            color: "var(--text-dim)",
            marginTop: 8,
          }}
        >
          window: last {burn_rate.window_days}d · {burn_rate.completions_in_window} completions
        </div>
      </div>

      {/* Exit gate checklist */}
      {exit_gate_checklist.length > 0 && (
        <div className="card" style={{ padding: "12px 16px", marginBottom: 8 }}>
          <div
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: 9,
              color: "var(--text-dim)",
              textTransform: "uppercase",
              letterSpacing: "0.05em",
              marginBottom: 8,
            }}
          >
            Exit gate checklist
          </div>
          {exit_gate_checklist.map((item) => (
            <div
              key={item.name}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                padding: "5px 0",
                borderBottom: "1px solid var(--border-soft)",
              }}
            >
              <span
                style={{
                  fontFamily: "var(--font-mono)",
                  fontSize: 14,
                  fontWeight: 700,
                  color: statusColor(item.status),
                  minWidth: 16,
                }}
              >
                {statusIcon(item.status)}
              </span>
              <span
                style={{
                  fontFamily: "var(--font-mono)",
                  fontSize: 11,
                  color: "var(--text)",
                  flex: 1,
                }}
              >
                {item.name}
              </span>
              {item.current_value && (
                <span style={{ fontFamily: "var(--font-mono)", fontSize: 10, color: "var(--text-dim)" }}>
                  {item.current_value}
                </span>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Milestone trajectories */}
      {milestone_trajectories.length > 0 && (
        <div className="card" style={{ padding: "12px 16px" }}>
          <div
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: 9,
              color: "var(--text-dim)",
              textTransform: "uppercase",
              letterSpacing: "0.05em",
              marginBottom: 8,
            }}
          >
            Milestone trajectories
          </div>
          {milestone_trajectories.map((m) => (
            <MilestoneTrajectoryRow key={m.milestone_id} m={m} />
          ))}
        </div>
      )}
    </div>
  );
}
